import type { ReactNode } from "react";
import { Truck, Store, Zap } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { DispatchOrder } from "../../types/Dispatch";
import { DispatchPointsPill } from "./dispatch-session-chrome";

type Props = {
    order: DispatchOrder;
    /** Units counted towards the order: resolved on the pack screen,
     *  shipping on Confirm Shipment. */
    done: number;
    total: number;
    /** Units shipping short (missing / damaged / wrong). Drawn amber on the
     *  progress strip; only Confirm Shipment passes it. */
    short?: number;
    weightKg: number;
    /** Buttons on the right (Reset, Flag, back). Omitted on the flag
     *  confirmation, which has its own Cancel. */
    actions?: ReactNode;
};

/** The order header shared by the pack screen, Confirm Shipment and the
 *  flag confirmation, so the packer sees the same order context at every
 *  step: number, channel, delivery type, unit count, weight, points, then
 *  the actions. The progress bar is the bar's bottom edge.
 *
 *  Replaces the old stack of title bar + summary card + progress bar,
 *  which cost ~160px of height before the first item line — on the 768px
 *  Polish bench screens that left room for under two lines. */
const DispatchOrderBar = ({ order, done, total, short = 0, weightKg, actions }: Props) => {
    const { t } = useTranslation();

    // Express / Priority are time-critical, so they get a solid gold pill;
    // Standard stays plain. Collection = counter pick-up, no courier label.
    const dt = (order.delivery_type ?? "").toLowerCase();
    const showDelivery = dt === "standard" || dt === "express" || dt === "priority" || dt === "collection";
    const DeliveryIcon = dt === "standard" ? Truck : dt === "collection" ? Store : Zap;

    const complete = total > 0 && done + short >= total;
    const pct = (n: number) => (total > 0 ? Math.min(100, (n / total) * 100) : 0);

    return (
        <div className="dispatch-order-bar">
            <div className="dispatch-order-bar-row">
                <h1 className="dispatch-order-bar-title">
                    {t("order_title_label")} #<span className="dispatch-order-bar-number">{order.order_number}</span>
                </h1>
                {order.sales_channel && (
                    <span className="dispatch-platform-badge">{order.sales_channel}</span>
                )}
                {showDelivery && (
                    <span className={`dispatch-ship-badge dispatch-ship-badge--${dt}`}>
                        <DeliveryIcon size={16} strokeWidth={2.4} aria-hidden="true" />
                        {t(`delivery_type_${dt}`)}
                    </span>
                )}
                <div className="dispatch-order-bar-spacer" />
                <div className="dispatch-stat">
                    <span className="dispatch-stat-label">{t("dispatch_stat_items")}</span>
                    <span
                        className={`dispatch-stat-value dispatch-stat-value--count${complete ? " dispatch-stat-value--done" : ""}`}
                        data-testid="dispatch-order-count"
                    >
                        {done}
                        <span className="dispatch-stat-total"> / {total}</span>
                    </span>
                </div>
                <div className="dispatch-stat">
                    <span className="dispatch-stat-label">{t("weight_label")}</span>
                    <span className="dispatch-stat-value dispatch-stat-value--weight">
                        {weightKg.toFixed(1)} kg
                    </span>
                </div>
                <DispatchPointsPill />
                {actions && (
                    <>
                        <span className="dispatch-order-bar-divider" aria-hidden="true" />
                        <div className="dispatch-order-bar-actions">{actions}</div>
                    </>
                )}
            </div>
            <div className="dispatch-order-bar-progress" aria-hidden="true">
                <div className="dispatch-progress-fill" style={{ width: `${pct(done)}%` }} />
                {short > 0 && (
                    <div className="dispatch-order-bar-progress-short" style={{ width: `${pct(short)}%` }} />
                )}
            </div>
        </div>
    );
};

export default DispatchOrderBar;
